/** A parcel row as the create-order form holds it — every field a raw string. */
export interface ParcelRowLike {
  code?: string;
  description?: string;
  weight_kg?: string;
}

/**
 * Sum of the parcel weights, in kg.
 *
 * With parcels enabled the order's «Вес, кг» IS this number (owner ruling,
 * 2026-08-25) — the field is computed, not hand-edited. That makes this the
 * single source of truth for three places that must never disagree: the
 * read-only weight input, the parcels block footer («4 места · 12 кг») and the
 * dialog footer hint. Deriving each of them separately is exactly how those
 * three numbers drift apart.
 *
 * A blank or non-numeric weight counts as 0 rather than poisoning the total
 * with `NaN`: a dispatcher part-way through typing a row must not blank out the
 * whole sum. `Number("")` is 0, so the emptiness check is explicit.
 */
export function sumParcelWeight(rows: ParcelRowLike[] | undefined): number {
  if (!rows?.length) return 0;
  return rows.reduce((total, row) => {
    const raw = (row?.weight_kg ?? "").trim();
    if (raw === "") return total;
    const value = Number(raw);
    return Number.isFinite(value) ? total + value : total;
  }, 0);
}

/**
 * Formats a kg total for display.
 *
 * Rounded to three decimals — the `Decimal(_, 3)` precision of the `weight_kg`
 * column. Showing more would promise precision the database does not store, and
 * floating-point addition readily produces it (0.1 + 0.2 = 0.30000000000000004).
 * `String()` of the rounded number then drops trailing zeros on its own, so an
 * integer total renders as «12» rather than «12.000», which is both what the
 * mockup shows and what a dispatcher would write.
 */
export function formatParcelWeight(total: number): string {
  if (!Number.isFinite(total)) return "0";
  return String(Math.round(total * 1000) / 1000);
}
